import React, { useEffect, useMemo, useState } from "react";
import { fetchEquityAudit } from "../api";
import { Card, Loading, ErrorState, EmptyState, Select } from "./ui";
import { money0, num } from "../lib/format";

/**
 * The audit trail behind the equity split.
 *
 * Two ways in, because there are two questions people actually ask:
 *
 *   By partner - "where did MY figures come from?" Their years, the four
 *                measures in each, and every project underneath.
 *   By client  - "who brought this one in?" One row per project-year, showing
 *                who sourced it and who ran it side by side.
 *
 * The lines are the same rows the equity page is built from, aggregated to
 * year x project x originator x team, so the totals here add back to the
 * headline exactly rather than being a second opinion.
 */

const YEARS = ["2022", "2023", "2024", "2025", "2026"];

export default function EquityAudit({ label, partners }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [view, setView] = useState("partner");
  const [who, setWho] = useState("");
  const [client, setClient] = useState("");
  // Which capacity the project list is showing: all, or one of the three.
  const [kind, setKind] = useState("all");

  useEffect(() => {
    fetchEquityAudit().then((d) => setRows(d.rows)).catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!who && partners.length) setWho(partners[0]);
  }, [partners, who]);
  useEffect(() => { setKind("all"); }, [who]);

  /** The four measures per year for one partner, rebuilt from the raw lines. */
  const byYear = useMemo(() => {
    if (!rows || !who) return [];
    return YEARS.map((y) => {
      const m = { ro: 0, rg: 0, go: 0, gf: 0, rf: 0 };
      for (const r of rows) {
        if (r.year !== y) continue;
        const rev = num(r.revenue), gp = num(r.gp);
        if (r.genesis === who && r.team === who) { m.ro += rev; m.go += gp; }
        else if (r.genesis === who) { m.rg += rev; }
        else if (r.team === who) { m.rf += rev; m.gf += gp; }
      }
      return { year: y, ...m };
    }).filter((y) => y.ro || y.rg || y.gf || y.rf);
  }, [rows, who]);

  /** Every project this partner touched, and in which capacity. */
  const projects = useMemo(() => {
    if (!rows || !who) return [];
    return rows
      .filter((r) => r.genesis === who || r.team === who)
      .map((r) => ({
        ...r,
        kind: r.genesis === who && r.team === who ? "own"
            : r.genesis === who ? "passed" : "ran",
        role: r.genesis === who && r.team === who ? "brought in and ran"
            : r.genesis === who ? "brought in, ran by " + (r.team || "?").toUpperCase()
            : "ran for " + (r.genesis || "?").toUpperCase(),
      }))
      .sort((a, b) => num(b.revenue) - num(a.revenue));
  }, [rows, who]);

  /**
   * The client list, alphabetical. A native select carries its own type-ahead,
   * so typing "hog" jumps to Hogan Lovells without a search box - which is why
   * the label has to start with the client's name.
   */
  const clientList = useMemo(() => {
    if (!rows) return [];
    const m = new Map();
    for (const r of rows) {
      const k = r.client || "(no client)";
      m.set(k, (m.get(k) || 0) + num(r.revenue));
    }
    return [...m.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, revenue]) => ({ name, revenue }));
  }, [rows]);

  /** One row per project-year for whichever client is selected. */
  const clientRows = useMemo(() => {
    if (!rows) return [];
    return rows
      .filter((r) => !client || (r.client || "(no client)") === client)
      .sort((a, b) => (a.year === b.year
        ? num(b.revenue) - num(a.revenue)
        : a.year.localeCompare(b.year)));
  }, [rows, client]);

  /** Who sourced this client's work, and how much of it. */
  const sourcedBy = useMemo(() => {
    if (!client) return [];
    const m = new Map();
    for (const r of clientRows) {
      const k = r.genesis || "?";
      m.set(k, (m.get(k) || 0) + num(r.revenue));
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [clientRows, client]);

  /** The three capacities, with what each is worth - shown on the buttons. */
  const KINDS = [
    { key: "all",    label: "All" },
    { key: "own",    label: "Brought in and ran" },
    { key: "passed", label: "Brought in, ran by others" },
    { key: "ran",    label: "Ran for others" },
  ].map((k) => {
    const set = k.key === "all" ? projects : projects.filter((r) => r.kind === k.key);
    return { ...k, count: set.length, revenue: set.reduce((t, r) => t + num(r.revenue), 0) };
  });
  const shown = kind === "all" ? projects : projects.filter((r) => r.kind === kind);

  if (error) return <Card><ErrorState message={error} /></Card>;
  if (!rows) return <Card><Loading label="Loading the underlying lines…" /></Card>;

  const total = (k) => byYear.reduce((t, y) => t + y[k], 0);

  return (
    <Card
      className="eq-audit-card"
      title="Audit"
      note="the invoice lines behind the split — these add back to the figures above"
      actions={
        <div className="seg" role="group" aria-label="Audit view">
          <button className={`seg-btn${view === "partner" ? " is-on" : ""}`}
                  onClick={() => setView("partner")}>By partner</button>
          <button className={`seg-btn${view === "client" ? " is-on" : ""}`}
                  onClick={() => setView("client")}>By client</button>
        </div>
      }
    >
      {view === "partner" ? (
        <>
          <div className="toolbar">
            <Select value={who} onChange={setWho}
                    options={partners.map((u) => ({ value: u, label: label(u) }))}
                    style={{ minWidth: 240, maxWidth: "100%" }} />
          </div>

          {!byYear.length ? (
            <EmptyState title="Nothing recorded" hint="This partner has no revenue in these years." />
          ) : (
            <>
              <div className="tbl-wrap tbl-wrap--flush">
                <table className="tbl tbl-zebra eq-audit-tbl">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th className="num">Genesis — kept</th>
                      <th className="num">Own profit</th>
                      <th className="num">Genesis — passed on</th>
                      <th className="num">Ran for others</th>
                      <th className="num">Profit for others</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byYear.map((y) => (
                      <tr key={y.year}>
                        <td>{y.year}</td>
                        <td className="num">{money0(y.ro)}</td>
                        <td className={`num ${y.go < 0 ? "neg" : ""}`}>{money0(y.go)}</td>
                        <td className="num">{money0(y.rg)}</td>
                        <td className="num muted">{money0(y.rf)}</td>
                        <td className={`num ${y.gf < 0 ? "neg" : ""}`}>{money0(y.gf)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><strong>Total</strong></td>
                      <td className="num"><strong>{money0(total("ro"))}</strong></td>
                      <td className="num"><strong>{money0(total("go"))}</strong></td>
                      <td className="num"><strong>{money0(total("rg"))}</strong></td>
                      <td className="num muted">{money0(total("rf"))}</td>
                      <td className="num"><strong>{money0(total("gf"))}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="toolbar" style={{ marginTop: 12 }}>
                <div className="seg" role="group" aria-label="Capacity">
                  {KINDS.map((k) => (
                    <button key={k.key}
                            className={`seg-btn${kind === k.key ? " is-on" : ""}`}
                            disabled={!k.count}
                            onClick={() => setKind(k.key)}>
                      {k.label}
                      <span className="seg-count">{k.count}</span>
                    </button>
                  ))}
                </div>
                <span className="muted">
                  {money0(KINDS.find((k) => k.key === kind).revenue)} across
                  {" "}{shown.length} project{shown.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="tbl-wrap tbl-wrap--flush eq-audit-scroll">
                <table className="tbl tbl-zebra eq-audit-tbl">
                  <thead>
                    <tr>
                      <th>Year</th><th>Client</th><th>Project</th>
                      <th>Capacity</th><th className="num">Lines</th>
                      <th className="num">Revenue</th><th className="num">GP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r, i) => (
                      <tr key={`${r.year}-${r.project}-${r.genesis}-${r.team}-${i}`}>
                        <td>{r.year}</td>
                        <td>{r.client}</td>
                        <td title={r.project}>{r.name}</td>
                        <td className="muted">{r.role}</td>
                        <td className="num muted">{r.lines}</td>
                        <td className="num">{money0(num(r.revenue))}</td>
                        <td className={`num ${num(r.gp) < 0 ? "neg" : ""}`}>{money0(num(r.gp))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="toolbar">
            <Select value={client} onChange={setClient} style={{ minWidth: 380, maxWidth: "100%" }}
                    options={[{ value: "", label: `All clients (${clientList.length})` }].concat(
                      clientList.map((c) => ({
                        value: c.name,
                        label: `${c.name} — ${money0(c.revenue)}`,
                      })))} />
            <span className="muted">
              {clientRows.length} row{clientRows.length === 1 ? "" : "s"}
              {client && sourcedBy.length > 0 && (
                <> · brought in by {sourcedBy.map(([u, v]) =>
                  `${label(u)} ${money0(v)}`).join(", ")}</>
              )}
            </span>
          </div>
          <div className="tbl-wrap tbl-wrap--flush eq-audit-scroll">
            <table className="tbl tbl-zebra eq-audit-tbl">
              <thead>
                <tr>
                  <th>Year</th><th>Client</th><th>Project</th>
                  <th>Brought in by</th><th>Run by</th>
                  <th className="num">Lines</th>
                  <th className="num">Revenue</th><th className="num">GP</th>
                </tr>
              </thead>
              <tbody>
                {clientRows.map((r, i) => (
                  <tr key={`${r.year}-${r.project}-${r.genesis}-${r.team}-${i}`}>
                    <td>{r.year}</td>
                    <td>{r.client}</td>
                    <td title={r.project}>{r.name}</td>
                    <td>
                      {label(r.genesis)}
                      {r.genesis_code && r.genesis_code !== (r.genesis || "").toUpperCase() && (
                        <span className="muted"> · {r.genesis_code}</span>
                      )}
                    </td>
                    <td className={r.team === r.genesis ? "muted" : ""}>{label(r.team)}</td>
                    <td className="num muted">{r.lines}</td>
                    <td className="num">{money0(num(r.revenue))}</td>
                    <td className={`num ${num(r.gp) < 0 ? "neg" : ""}`}>{money0(num(r.gp))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="toolbar-note">
            One row per project and year. Where <em>brought in by</em> and <em>run by</em>
            {" "}differ, the work changed hands — that is what the genesis weights pay for.
            A genesis code that is not a login is shown as it appears in the ledger. With the
            list open, type the first letters of a client to jump to it.
          </p>
        </>
      )}
    </Card>
  );
}
